/**
 * Stream settings — persisted defaults for deposit + rate.
 *
 * Stored in localStorage so the user can tweak via the settings screen
 * and the call flow starts immediately without a config gate.
 */

import { parseTokenAmount } from '@tokenstreaming/shared';

const STORAGE_KEY = 'qr20:stream-settings';

export interface StreamSettings {
  /** Human-readable deposit amount (e.g. "0.001") */
  deposit: string;
  /** Human-readable rate per second (e.g. "0.00001") */
  ratePerSec: string;
  /** Token address override (empty = use chain default) */
  tokenAddress: string;
}

/** Option A defaults — negligible amounts for testing */
export const DEFAULT_STREAM_SETTINGS: StreamSettings = {
  deposit: '0.001',
  ratePerSec: '0.00001',
  tokenAddress: '',
};

export function loadStreamSettings(): StreamSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_STREAM_SETTINGS };
    const parsed = JSON.parse(raw) as Partial<StreamSettings>;
    return {
      deposit: parsed.deposit || DEFAULT_STREAM_SETTINGS.deposit,
      ratePerSec: parsed.ratePerSec || DEFAULT_STREAM_SETTINGS.ratePerSec,
      tokenAddress: parsed.tokenAddress ?? DEFAULT_STREAM_SETTINGS.tokenAddress,
    };
  } catch {
    return { ...DEFAULT_STREAM_SETTINGS };
  }
}

export function saveStreamSettings(settings: StreamSettings): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
}

/**
 * Resolve settings into atomic bigint values for the engine.
 */
export function resolveStreamConfig(settings: StreamSettings, decimals: number) {
  return {
    deposit: parseTokenAmount(settings.deposit, decimals),
    ratePerSecAtomic: parseTokenAmount(settings.ratePerSec, decimals),
  };
}
